import styled from '@emotion/styled'
import { IconButton } from '@mui/material'
import React, { useEffect, useState } from 'react'

import { ImageGallery } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGallery'
import { useImageGalleryContext } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGalleryContext'
import { TabContent } from '@f-fertighaus/applications/Next/components/common/navigation/tabs/TabContent'
import { TabLabelWithWT } from '@f-fertighaus/applications/Next/components/common/navigation/tabs/TabLabel'
import { TabsList } from '@f-fertighaus/applications/Next/components/common/navigation/tabs/TabsList'
import { HeartFilledIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/HeartFilledIcon'
import { HeartIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/HeartIcon'
import { t } from '@f-fertighaus/applications/Next/public/locales/de'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { FloorPlansTab } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseImageGallery/FloorPlansTab'
import { GalleryHouseImagesTab } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseImageGallery/GalleryHouseImagesTab'
import { VideosTab } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseImageGallery/VideosTab'
import { useUserDetails } from '@f-fertighaus/applications/Next/views/houseDetailPage/HouseSections/sections/VendorDetailsSection/hooks/useUserDetails'

export enum ImagesTab {
  houseImages,
  floorPlans,
  videos
}

export const HouseImageGallery = () => {
  const {
    state: { name, floor_plans, vendor_nid, active, videos },
    favoriteHouse: { isFavorite, addToFavorites, removeFromFavorites },
    featureFlags
  } = useHouseDetail()
  const {
    state: { galleryOpen, initialTabIndex }
  } = useImageGalleryContext()
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const userDetails = useUserDetails()

  useEffect(() => {
    if (
      galleryOpen &&
      initialTabIndex !== null &&
      initialTabIndex !== undefined
    ) {
      setActiveTabIndex(initialTabIndex)
    }
  }, [initialTabIndex, galleryOpen])

  const handleFavouriteButtonClick = () => {
    webtrack({
      category: 'CTA-button',
      action: 'click',
      label: 'favourite-house',
      location: 'image-gallery',
      values: [{ type: 'text', value: '(ICON)' }]
    })
    if (userDetails?.ua_user_state) {
      if (isFavorite) {
        removeFromFavorites()
      } else {
        addToFavorites()
      }
    }
  }

  const favouriteButton = userDetails?.ua_user_state ? (
    <IconButton
      data-isfavorite={isFavorite}
      data-sel='btn-fav'
      aria-label='Speichern'
      onClick={handleFavouriteButtonClick}
      sx={{ width: '100%', height: '100%' }}>
      {isFavorite ? <HeartFilledIcon /> : <HeartIcon />}
    </IconButton>
  ) : (
    <IconButton
      className='js-request-button'
      data-request-type='house-wizard-item-favourite'
      data-funnel-variant='house-wizard-item-favourite'
      aria-label='Speichern'
      data-sel='btn-fav'
      {...(vendor_nid && { 'data-vendor-nid': vendor_nid })}
      onClick={handleFavouriteButtonClick}
      sx={{ width: '100%', height: '100%' }}>
      <HeartIcon />
    </IconButton>
  )

  const showAddToFavourite = !featureFlags.includes(
    '@FF_DISABLE_ADD_TO_FAVOURITE'
  )

  return (
    <ImageGallery open={galleryOpen}>
      <Content>
        <ImageGallery.Navigation
          title={name}
          ctaButton={active && showAddToFavourite ? favouriteButton : null}>
          <TabsList
            activeTabIndex={activeTabIndex}
            handleTabChange={(event, index) => setActiveTabIndex(index)}>
            <TabLabelWithWT
              tabIndex={0}
              label={t('houseDetailPage.image_gallery.house_images.tab_name')}
              index={ImagesTab.houseImages}
              wtPayload={{
                category: 'CTA-button',
                action: 'click',
                label: 'pictures-tab',
                location: 'image-gallery',
                values: [
                  {
                    type: 'text',
                    value: t(
                      'houseDetailPage.image_gallery.house_images.tab_name'
                    )
                  }
                ]
              }}
            />
            <TabLabelWithWT
              tabIndex={0}
              label={t('houseDetailPage.image_gallery.floor_plans.tab_name')}
              index={ImagesTab.floorPlans}
              wtPayload={{
                category: 'CTA-button',
                action: 'click',
                label: 'floor-plan-tab',
                location: 'image-gallery',
                values: [
                  {
                    type: 'text',
                    value: t(
                      'houseDetailPage.image_gallery.floor_plans.tab_name'
                    )
                  }
                ]
              }}
              disabled={!floor_plans?.length}
            />
            {videos && videos.length > 0 && (
              <TabLabelWithWT
                tabIndex={0}
                label={t('houseDetailPage.image_gallery.videos.tab_name')}
                index={ImagesTab.videos}
                wtPayload={{
                  category: 'CTA-button',
                  action: 'click',
                  label: 'videos-tab',
                  location: 'image-gallery',
                  values: [
                    {
                      type: 'text',
                      value: t('houseDetailPage.image_gallery.videos.tab_name')
                    }
                  ]
                }}
              />
            )}
          </TabsList>
        </ImageGallery.Navigation>
        <TabContent
          index={ImagesTab.houseImages}
          activeTabIndex={activeTabIndex}>
          <GalleryHouseImagesTab />
        </TabContent>
        <TabContent
          index={ImagesTab.floorPlans}
          activeTabIndex={activeTabIndex}>
          <FloorPlansTab />
        </TabContent>
        <TabContent index={ImagesTab.videos} activeTabIndex={activeTabIndex}>
          <VideosTab />
        </TabContent>
        <ImageGallery.FocusView />
      </Content>
    </ImageGallery>
  )
}

const Content = styled.div`
  max-width: 1160px;
  width: 100%;
  margin: 0 auto;
`
